// WishArchiveTable — desktop table surface for /wishes/archive (per
// `docs/spec/wishlist/DESIGN.md §5.8`).
//
// Columns: title / category / completed_at / progress_percent / actions.
// Rows can be expanded into a filter rail via WishArchiveFilter (separate
// component).
//
// Mobile fallback (W33.4 polish): a collapsible timeline — deferred; the
// W33.4 commit ships the desktop table only. The mobile rendering is a
// future W34.0 polish item.
//
// W34.2.2: per-row mutations fire useToast() feedback per SPEC-DRIFT-CHECK
// §13.1.11 follow-up (restore = success variant; delete = danger variant).

import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, RotateCcw, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/toast";
import { UndoBar } from "@/components/patterns/UndoBar";
import { useUndoBar } from "@/hooks/useUndoBar";
import { cn } from "@/lib/utils/cn";
import { WISH_RESTORE_DAYS } from "@/lib/retentionWindows";
import {
  patchWish,
  deleteWish,
  restoreWish,
  type WishCategory,
  type WishPriority,
  type WishSummary,
} from "@/lib/api/wishlist";
import { queryKeys } from "@/lib/queries/keys";

export interface WishArchiveTableProps {
  items: WishSummary[];
  className?: string;
}

const CATEGORY_LABEL: Record<WishCategory, string> = {
  general: "一般",
  savings: "儲蓄",
  travel: "旅行",
  event: "事件",
  purchase: "購物",
  bucket_list: "願望清單",
  custom: "自訂",
};

const PRIORITY_LABEL: Record<WishPriority, string> = {
  must: "必做",
  want: "想要",
  maybe: "也許",
};

function formatCompletedAt(iso: string | null): string {
  if (!iso) return "—";
  try {
    return iso.slice(0, 10);
  } catch {
    return "—";
  }
}

export function WishArchiveTable({ items, className }: WishArchiveTableProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  // R1 (ADR-0080) follow-up — see the comment on the delete mutation below.
  const undo = useUndoBar<{ id: string; name: string }>();
  // Named undoRestoreMut, not restoreMut: this file already has a
  // restoreMut for 「恢復到進行中」 (patchWish → status), which is a
  // different operation from undoing a delete.
  const undoRestoreMut = useMutation({
    mutationFn: (id: string) => restoreWish(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "archive"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  const restoreMut = useMutation({
    mutationFn: async (wish: WishSummary) =>
      patchWish(wish.id, { status: "in_progress" }),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "archive"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      toast.push({
        message: `已恢復「${vars.name}」到進行中`,
        variant: "success",
      });
    },
    onError: (_e, vars) => {
      toast.push({
        message: `恢復「${vars.name}」失敗，請稍後再試`,
        variant: "danger",
      });
    },
  });
  const deleteMut = useMutation({
    mutationFn: async (wish: WishSummary) => deleteWish(wish.id),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "archive"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      // R1 (ADR-0080) follow-up: this used to toast 「已永久刪除」, and the
      // button said 永久刪除 with a dialog promising 「7 天後將從資料庫清除」 —
      // all three describing a hard delete. `deleteWish` is a **soft** delete
      // and `restoreWish` exists; the wishlist has no hard-delete route at all
      // (verified against the router table). The copy now says what happens.
      undo.begin({ id: vars.id, name: vars.name });
    },
    onError: (_e, vars) => {
      toast.push({
        message: `刪除「${vars.name}」失敗，請稍後再試`,
        variant: "danger",
      });
    },
  });

  // R1 (ADR-0080) follow-up. The dialog's copy said 永久刪除 / 7 天後將從資料庫
  // 清除 over a `deleteWish` that is a soft delete with a working
  // `restoreWish` — there is no hard-delete route in the wishlist module at
  // all. It now names the window that actually exists.
  //
  // W56.0 J (ADR-0201): the dialog is **gone**. A soft delete with a working
  // restore does not get a second interception in front of it, and the dialog
  // only existed here because it "also rendered from the empty state, where
  // there is no row for the undo bar to sit on" — which was never true: the
  // bar is `position: fixed`, so it has no row to sit on and no row to move.
  const undoSlot = undo.item ? (
    <div
      data-testid="wish-archive-undo-slot"
      className="pointer-events-none fixed inset-x-0 bottom-[108px] z-40 flex justify-center px-4"
    >
      <div className="pointer-events-auto relative w-full max-w-md">
        <UndoBar
          open
          message={`已刪除「${undo.item.name}」，${WISH_RESTORE_DAYS} 天內可還原`}
          onUndo={() => {
            undoRestoreMut.mutate(undo.item!.id);
            undo.clear();
          }}
          className="inset-x-0"
        />
      </div>
    </div>
  ) : null;

  if (items.length === 0) {
    return (
      <>
        <p
          data-testid="wish-archive-empty"
          className="text-body text-ink-3 py-s-6 text-center"
        >
          沒有符合條件的已實現心願
        </p>
        {undoSlot}
      </>
    );
  }

  return (
    <div
      data-component="WishArchiveTable"
      data-testid="wish-archive-table"
      className={cn(
        "surface-plain border border-hairline rounded-2xl overflow-hidden shadow-card",
        className,
      )}
    >
      <table className="w-full text-body-sm">
        <thead>
          <tr className="border-b border-hairline text-ink-3 text-meta uppercase tracking-wider">
            <th className="text-left px-s-4 py-s-3 font-semibold">標題</th>
            <th className="text-left px-s-4 py-s-3 font-semibold">類別</th>
            <th className="text-left px-s-4 py-s-3 font-semibold">優先度</th>
            <th className="text-left px-s-4 py-s-3 font-semibold">完成於</th>
            <th className="text-left px-s-4 py-s-3 font-semibold">動作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((w) => (
            <tr
              key={w.id}
              data-component="WishArchiveRow"
              data-wish-id={w.id}
              data-testid={`wish-archive-row-${w.id}`}
              className="border-b border-hairline last:border-b-0 hover:bg-paper-deep"
            >
              <td className="px-s-4 py-s-3 font-semibold text-ink-1 truncate max-w-[260px]">
                {w.name}
              </td>
              <td className="px-s-4 py-s-3 text-ink-2">
                {CATEGORY_LABEL[w.category] ?? w.category}
              </td>
              <td className="px-s-4 py-s-3 text-ink-2">
                {PRIORITY_LABEL[w.priority] ?? w.priority}
              </td>
              <td className="px-s-4 py-s-3 text-ink-2 tabular-nums">
                {formatCompletedAt(w.completed_at)}
              </td>
              <td className="px-s-4 py-s-3">
                <div className="flex items-center gap-s-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/wishes/${w.id}`)}
                    data-testid={`wish-archive-view-${w.id}`}
                    className="press min-h-11 inline-flex items-center gap-1.5 px-s-3 rounded-full text-ink-1 hover:bg-paper-calm"
                  >
                    <ArrowRight className="h-4 w-4" aria-hidden />
                    查看詳情
                  </button>
                  <button
                    type="button"
                    disabled={restoreMut.isPending}
                    onClick={() => restoreMut.mutate(w)}
                    data-testid={`wish-archive-restore-${w.id}`}
                    className="press min-h-11 inline-flex items-center gap-1.5 px-s-3 rounded-full text-you-strong hover:bg-paper-calm disabled:opacity-50"
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden />
                    恢復到進行中
                  </button>
                  <button
                    type="button"
                    disabled={deleteMut.isPending}
                    onClick={() => deleteMut.mutate(w)}
                    data-testid={`wish-archive-delete-${w.id}`}
                    // W56.0 J (ADR-0201) — the label names what happens,
                    // including the window, because the confirm dialog that
                    // used to carry that sentence is gone.
                    className="press min-h-11 inline-flex items-center gap-1.5 px-s-3 rounded-full text-ink-1 hover:bg-paper-calm disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    刪除並保留 {WISH_RESTORE_DAYS} 天
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {undoSlot}
    </div>
  );
}
